import React, {CSSProperties, ReactNode, useState} from 'react'
import {Icon} from '../icon/icon'
import styled, {css} from 'styled-components'
import {Tooltip} from '../tooltip/tooltip'

type Size = 'small' | 'large'

interface CheckboxProps {
  onChange: (
    checked: boolean,
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
      | React.KeyboardEvent<HTMLDivElement>
  ) => void
  checked: boolean
  label?: ReactNode
  labelPosition?: 'left' | 'right'
  title?: string
  secondary?: string
  thumbnail?: React.ReactNode
  disabled?: boolean
  disabledReason?: string
  size?: Size
  asRadio?: boolean
  style?: CSSProperties
}

// Hide checkbox visually but remain accessible to screen readers.
// Source: https://polished.js.org/docs/#hidevisually
const HiddenCheckbox = styled.input.attrs({type: 'checkbox'})`
  position: absolute;
  opacity: 0;
  width: 0px;
  height: 0px;
`

const Thumbnail = styled.div<{checked: boolean}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 60px;
  left: 0;
  pointer-events: none;
  ${p => p.checked && `opacity: 0;`}
`

const StyledIcon = styled(Icon)``

const StyledCheckbox = styled.div<{
  checked: boolean
  focused: boolean
  disabled: boolean
  size: Size
  asRadio: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${p => (p.size === 'small' ? '18px' : '20px')};
  width: ${p => (p.size === 'small' ? '18px' : '20px')};
  flex: none;
  border: 1px solid ${props => props.theme.grey20};
  border-radius: ${({asRadio, theme}) =>
    asRadio ? '50%' : theme.borderRadiusS};
  background-color: ${({checked, theme, disabled}) =>
    disabled
      ? checked
        ? theme.grey50
        : theme.grey05
      : checked
      ? theme.blue
      : theme.grey05};
  color: ${({checked, theme}) => (checked ? theme.white : theme.black)};
  transition: all 0.1s ease;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};

  ${({disabled, checked, theme}) =>
    !disabled &&
    css`
      &:hover,
      &:focus {
        background-color: ${checked ? theme.blueDark : theme.grey05};
      }
      &:active {
        background-color: ${checked ? theme.blueDarkest : theme.grey10};
      }
    `}

  ${StyledIcon} {
    color: white;
    opacity: ${props => (props.checked ? '1' : '0')};
  }
`

// not a <label> cos we are wrapping the whole thing in one of those
export const OuterLabel = styled.div<{
  label: 'left' | 'right'
  disabled: boolean
  size: Size
}>`
  display: flex;
  font-size: ${({theme, size}) =>
    size === 'small' ? theme.fontSizeS : theme.fontSize};
  line-height: ${({theme, size}) =>
    size === 'small' ? theme.lineHeightS : theme.lineHeight};
  align-items: center;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  overflow: hidden;
  ${props =>
    props.label === 'left'
      ? `padding: 5px ${props.size === 'small' ? '8px' : '20px'} 5px 0px;`
      : `padding: 10px 0px 10px ${props.size === 'small' ? '8px' : '20px'};`};
`

export const PrimaryLabel = styled.div<{fill?: boolean}>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${p => p.fill && 'flex: 1;'};
`

export const SecondaryLabel = styled.div`
  margin-left: 5px;
  color: ${({theme}) => theme.grey50};
  white-space: nowrap;
`

const CheckboxContainer = styled.label<{disabled: boolean}>`
  display: inline-flex;
  align-items: center;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  position: relative;
  &:hover ${Thumbnail} {
    display: none;
  }
`

export const Checkbox = ({
  onChange,
  checked,
  label,
  labelPosition = 'right',
  secondary,
  thumbnail,
  disabled = false,
  size = 'large',
  asRadio = false,
  ...props
}: CheckboxProps) => {
  const [focused, setFocused] = useState<boolean>(false)
  const checkBoxContainer = (
    <CheckboxContainer disabled={disabled} {...props}>
      {label && labelPosition === 'left' && (
        <OuterLabel label="left" disabled={disabled} size={size}>
          <PrimaryLabel>{label}</PrimaryLabel>
          {secondary && <SecondaryLabel>{secondary}</SecondaryLabel>}
        </OuterLabel>
      )}
      {thumbnail && labelPosition === 'right' && (
        <Thumbnail checked={checked}>{thumbnail}</Thumbnail>
      )}
      <HiddenCheckbox
        checked={checked}
        disabled={disabled}
        {...props}
        onChange={e => {
          onChange(e.target.checked, e)
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      <StyledCheckbox
        focused={focused}
        checked={checked}
        disabled={disabled}
        size={size}
        asRadio={asRadio}
      >
        <StyledIcon name={asRadio ? 'radio' : 'check_small'} />
      </StyledCheckbox>
      {label && labelPosition === 'right' && (
        <OuterLabel label="right" disabled={disabled} size={size}>
          <PrimaryLabel>{label}</PrimaryLabel>
          {secondary && <SecondaryLabel>{secondary}</SecondaryLabel>}
        </OuterLabel>
      )}
    </CheckboxContainer>
  )

  return (
    <>
      {disabled && props.disabledReason ? (
        <Tooltip
          content={props.disabledReason}
          isLight={false}
          position={'top'}
          offsetTop={'-10px'}
        >
          {checkBoxContainer}
        </Tooltip>
      ) : (
        checkBoxContainer
      )}
    </>
  )
}
